import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { ArrowLeft, Save, RefreshCw, Image as ImageIcon, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { DEFAULT_CONTENT } from "../content/defaults";

/**
 * Admin CMS page — edit contact info, founder photo, home hero copy and
 * all services. Changes are persisted via PUT /api/content.
 */
export default function AdminContent() {
  const { authAxios, user, logout } = useAuth();
  const { refresh } = useContent();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Site Content | Fourbees Admin";
    (async () => {
      try {
        const res = await authAxios.get("/content");
        setDoc({ ...DEFAULT_CONTENT, ...res.data });
      } catch (e) {
        toast.error("Couldn't load content — showing defaults.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateContact = (field) => (e) =>
    setDoc((p) => ({ ...p, contact: { ...p.contact, [field]: e.target.value } }));

  const updateHome = (field) => (e) =>
    setDoc((p) => ({ ...p, home: { ...p.home, [field]: e.target.value } }));

  const updateFounderPhoto = (e) =>
    setDoc((p) => ({ ...p, founderPhoto: e.target.value }));

  const updateService = (idx, field) => (e) =>
    setDoc((p) => ({
      ...p,
      services: p.services.map((s, i) => (i === idx ? { ...s, [field]: e.target.value } : s)),
    }));

  const addService = () =>
    setDoc((p) => ({
      ...p,
      services: [
        ...p.services,
        {
          slug: `new-service-${p.services.length + 1}`,
          index: String(p.services.length + 1).padStart(2, "0"),
          title: "New Service",
          short: "Short description shown on the home page and in cards.",
          long: "Long description shown on the services page.\n\nSeparate paragraphs with a blank line.",
        },
      ],
    }));

  const removeService = (idx) => {
    if (!window.confirm("Remove this service?")) return;
    setDoc((p) => ({ ...p, services: p.services.filter((_, i) => i !== idx) }));
  };

  const moveService = (idx, dir) => {
    setDoc((p) => {
      const arr = [...p.services];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return p;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      // Re-number
      arr.forEach((s, i) => (s.index = String(i + 1).padStart(2, "0")));
      return { ...p, services: arr };
    });
  };

  const resetToDefaults = async () => {
    if (!window.confirm("Reset all content to the original defaults? This cannot be undone until you save again.")) return;
    setDoc(DEFAULT_CONTENT);
    toast.success("Reset to defaults — don't forget to save.");
  };

  const save = async () => {
    setSaving(true);
    try {
      await authAxios.put("/content", doc);
      await refresh();
      toast.success("Content saved — your website is updated.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed — please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <RefreshCw className="animate-spin text-navy/40" size={24} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pb-24" data-testid="admin-content-page">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#F8F7F3",
            border: "1px solid #E9EDF0",
            color: "#14283F",
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />

      {/* Header */}
      <header className="bg-navy text-cream py-4 sticky top-0 z-40 shadow-sm">
        <div className="container-site flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <button
              onClick={() => navigate("/admin")}
              className="flex items-center gap-2 text-cream/70 hover:text-cream transition-colors"
              data-testid="content-back-link"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Enquiries</span>
            </button>
            <div className="h-6 w-px bg-cream/20" />
            <h1 className="font-heading text-xl font-bold truncate">Site Content</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-sm text-cream/70">{user?.email}</span>
            <button
              onClick={resetToDefaults}
              className="hidden md:inline-flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-sm transition-colors text-sm"
              data-testid="content-reset-btn"
            >
              <RefreshCw size={14} />
              Reset
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="btn-gold text-sm px-5 py-2.5 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="content-save-btn"
            >
              <Save size={14} />
              {saving ? "Saving..." : "Save Changes"}
            </button>
            <button
              onClick={logout}
              className="text-sm text-cream/60 hover:text-cream underline underline-offset-2"
              data-testid="content-logout-btn"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-site py-10 max-w-5xl mx-auto">
        {/* Tabs nav */}
        <nav className="flex gap-2 mb-8 border-b border-light-grey overflow-x-auto">
          {[
            { href: "#contact", label: "Contact Details" },
            { href: "#founder", label: "Founder Photo" },
            { href: "#hero", label: "Home Hero" },
            { href: "#services", label: "Services" },
          ].map((t) => (
            <a
              key={t.href}
              href={t.href}
              className="font-body text-sm text-navy/70 hover:text-navy border-b-2 border-transparent hover:border-gold px-4 py-3 whitespace-nowrap transition-colors"
              data-testid={`content-tab-${t.href.slice(1)}`}
            >
              {t.label}
            </a>
          ))}
        </nav>

        {/* Contact section */}
        <Section
          id="contact"
          title="Contact Details"
          help="These appear in the site footer, contact page and the mobile menu. Update them whenever anything changes."
          testid="section-contact"
        >
          <Grid>
            <Field label="Telephone">
              <input
                type="text"
                value={doc.contact.phone}
                onChange={updateContact("phone")}
                className="form-input"
                data-testid="content-contact-phone"
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={doc.contact.email}
                onChange={updateContact("email")}
                className="form-input"
                data-testid="content-contact-email"
              />
            </Field>
            <Field label="LinkedIn URL">
              <input
                type="url"
                value={doc.contact.linkedin}
                onChange={updateContact("linkedin")}
                className="form-input"
                data-testid="content-contact-linkedin"
              />
            </Field>
            <Field label="Location">
              <input
                type="text"
                value={doc.contact.location}
                onChange={updateContact("location")}
                className="form-input"
                data-testid="content-contact-location"
              />
            </Field>
            <Field label="Founder name">
              <input
                type="text"
                value={doc.contact.founderName}
                onChange={updateContact("founderName")}
                className="form-input"
                data-testid="content-contact-founder-name"
              />
            </Field>
            <Field label="Founder role">
              <input
                type="text"
                value={doc.contact.founderRole}
                onChange={updateContact("founderRole")}
                className="form-input"
                data-testid="content-contact-founder-role"
              />
            </Field>
          </Grid>
        </Section>

        {/* Founder photo */}
        <Section
          id="founder"
          title="Founder Photo"
          help="This image appears on the About page. To upload a new photo, drop it into the chat with Emergent and copy the URL it gives you back, then paste below."
          testid="section-founder"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-2">
              <Field label="Image URL">
                <input
                  type="url"
                  value={doc.founderPhoto}
                  onChange={updateFounderPhoto}
                  className="form-input"
                  placeholder="https://..."
                  data-testid="content-founder-photo-url"
                />
              </Field>
              <p className="mt-3 text-xs text-charcoal/60 leading-relaxed">
                Tip — a clear head-and-shoulders photo works best. Landscape or portrait orientation
                both work (the About page is designed around a portrait).
              </p>
            </div>
            <div>
              <label className="form-label">Preview</label>
              <div className="relative border border-light-grey rounded-sm bg-white overflow-hidden aspect-[3/4]">
                {doc.founderPhoto ? (
                  <img
                    src={doc.founderPhoto}
                    alt="Founder preview"
                    className="w-full h-full object-cover"
                    data-testid="content-founder-photo-preview"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-charcoal/30">
                    <ImageIcon size={28} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Section>

        {/* Home hero */}
        <Section
          id="hero"
          title="Home Hero"
          help="The opening section of the homepage. The headline displays in two parts — the second part shows in gold. Keep them short and punchy."
          testid="section-hero"
        >
          <Grid>
            <Field label="Eyebrow (small label above headline)" wide>
              <input
                type="text"
                value={doc.home.heroEyebrow}
                onChange={updateHome("heroEyebrow")}
                className="form-input"
                data-testid="content-home-eyebrow"
              />
            </Field>
            <Field label="Headline — part 1 (navy)">
              <input
                type="text"
                value={doc.home.heroHeadline}
                onChange={updateHome("heroHeadline")}
                className="form-input"
                data-testid="content-home-headline"
              />
            </Field>
            <Field label="Headline — part 2 (gold)">
              <input
                type="text"
                value={doc.home.heroHeadlineAccent}
                onChange={updateHome("heroHeadlineAccent")}
                className="form-input"
                data-testid="content-home-headline-accent"
              />
            </Field>
            <Field label="Supporting paragraph" wide>
              <textarea
                value={doc.home.heroSub}
                onChange={updateHome("heroSub")}
                rows={3}
                className="form-input resize-none"
                data-testid="content-home-sub"
              />
            </Field>
            <Field label="Credibility — headline">
              <input
                type="text"
                value={doc.home.credibilityHeadline}
                onChange={updateHome("credibilityHeadline")}
                className="form-input"
                data-testid="content-home-credibility-headline"
              />
            </Field>
            <Field label="Credibility — supporting text">
              <textarea
                value={doc.home.credibilityBody}
                onChange={updateHome("credibilityBody")}
                rows={3}
                className="form-input resize-none"
                data-testid="content-home-credibility-body"
              />
            </Field>
          </Grid>
        </Section>

        {/* Services */}
        <Section
          id="services"
          title="Services"
          help="These power the services list on the home page and the full /services page. Separate paragraphs in 'Long description' with a blank line."
          testid="section-services"
        >
          <div className="space-y-6">
            {doc.services.map((s, i) => (
              <div
                key={i}
                className="border border-light-grey rounded-sm p-6 bg-white"
                data-testid={`content-service-${i}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="section-number">{s.index}</span>
                    <input
                      type="text"
                      value={s.title}
                      onChange={updateService(i, "title")}
                      className="font-heading text-lg font-bold text-navy bg-transparent border-0 border-b border-transparent hover:border-light-grey focus:border-navy focus:outline-none px-1 py-1 min-w-[200px] md:min-w-[400px]"
                      data-testid={`content-service-${i}-title`}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => moveService(i, -1)}
                      disabled={i === 0}
                      className="p-1.5 text-navy/40 hover:text-navy disabled:opacity-30"
                      aria-label="Move up"
                      data-testid={`content-service-${i}-up`}
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveService(i, 1)}
                      disabled={i === doc.services.length - 1}
                      className="p-1.5 text-navy/40 hover:text-navy disabled:opacity-30"
                      aria-label="Move down"
                      data-testid={`content-service-${i}-down`}
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => removeService(i)}
                      className="p-1.5 text-red-500 hover:text-red-700 ml-2"
                      aria-label="Remove service"
                      data-testid={`content-service-${i}-remove`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <Grid>
                  <Field label="URL slug">
                    <input
                      type="text"
                      value={s.slug}
                      onChange={updateService(i, "slug")}
                      className="form-input font-mono text-xs"
                      data-testid={`content-service-${i}-slug`}
                    />
                  </Field>
                  <Field label="Index (shown as 01, 02…)">
                    <input
                      type="text"
                      value={s.index}
                      onChange={updateService(i, "index")}
                      className="form-input"
                      data-testid={`content-service-${i}-index`}
                    />
                  </Field>
                  <Field label="Short description (cards + home)" wide>
                    <textarea
                      value={s.short}
                      onChange={updateService(i, "short")}
                      rows={2}
                      className="form-input resize-none"
                      data-testid={`content-service-${i}-short`}
                    />
                  </Field>
                  <Field label="Long description (services page) — blank line = new paragraph" wide>
                    <textarea
                      value={s.long}
                      onChange={updateService(i, "long")}
                      rows={6}
                      className="form-input resize-none"
                      data-testid={`content-service-${i}-long`}
                    />
                  </Field>
                </Grid>
              </div>
            ))}
            <button
              onClick={addService}
              className="w-full border-2 border-dashed border-light-grey hover:border-gold text-navy/60 hover:text-navy py-5 rounded-sm flex items-center justify-center gap-2 font-body text-sm transition-colors"
              data-testid="content-add-service"
            >
              <Plus size={16} />
              Add another service
            </button>
          </div>
        </Section>

        {/* Save bar - sticky bottom */}
        <div className="mt-10 flex justify-end gap-3 sticky bottom-4">
          <button
            onClick={save}
            disabled={saving}
            className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed shadow-xl"
            data-testid="content-save-btn-bottom"
          >
            <Save size={14} />
            {saving ? "Saving..." : "Save all changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ id, title, help, children, testid }) {
  return (
    <section id={id} className="mb-14 scroll-mt-28" data-testid={testid}>
      <div className="mb-6">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-2">{title}</h2>
        {help && <p className="text-sm text-charcoal/60 leading-relaxed max-w-2xl">{help}</p>}
      </div>
      {children}
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>;
}

function Field({ label, children, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}
